import React, { useEffect, useState } from 'react';

import dayjs from '~/timeInit';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { formatDateLongISO, formatDateLongMonthWithMeridiemShort, formatDateSlashes } from '~/services/TimeService';

import type { TagDraftSchedule } from '../TagForm';
import { SELECT_ALL_VALUE } from '../constants';
import { useGetTagSchedules, useRegionOptions } from '../hooks';
import { DATAGRID_BORDERED_CONTAINER_SX, DATAGRID_EXPAND_TO_ROWS_SX, DATAGRID_HIDE_SEPARATORS_SX } from '../styles';

import { type TagScheduleFormData, getTagScheduleFormValues } from './TagScheduleForm';

interface TagSchedulesListProps {
  tagId?: string;
  schedules: Array<TagDraftSchedule>;
  persistOnSave?: boolean;
  onEditClick: (schedule: TagDraftSchedule) => void;
  editingSchedule: TagDraftSchedule | null;
  onCancelEdit: () => void;
  onSaveCreate: (data: TagScheduleFormData) => boolean | void | Promise<boolean | void>;
  onSaveEdit: (data: TagScheduleFormData) => boolean | void | Promise<boolean | void>;
  onEndSchedule?: (schedule: TagDraftSchedule) => void;
  isSavingInline?: boolean;
}

const CREATE_SCHEDULE_ROW_ID = '__create-schedule__';

function getScheduleFormValues(schedule?: TagDraftSchedule | null) {
  return getTagScheduleFormValues(
    schedule
      ? {
          brands: schedule.brands,
          regions: schedule.regions,
          activeStartDate: schedule.activeStartDate || null,
          activeEndDate: schedule.activeEndDate || null,
          travelStartDate: schedule.travelStartDate || null,
          travelEndDate: schedule.travelEndDate || null,
        }
      : undefined,
  );
}

export default function TagSchedulesList({
  tagId,
  schedules = [],
  persistOnSave = false,
  onEditClick,
  editingSchedule,
  onCancelEdit,
  onSaveCreate,
  onSaveEdit,
  onEndSchedule,
  isSavingInline = false,
}: TagSchedulesListProps) {
  const [createValues, setCreateValues] = useState<TagScheduleFormData>(() => getScheduleFormValues());
  const [editValues, setEditValues] = useState<TagScheduleFormData>(() => getScheduleFormValues());

  const { regionOptions, allRegionCodes } = useRegionOptions();

  useEffect(() => {
    setEditValues(getScheduleFormValues(editingSchedule));
  }, [editingSchedule]);

  const {
    data = undefined,
    isLoading = false,
    error = null,
  } = useGetTagSchedules(
    tagId && !persistOnSave
      ? {
          tagId,
          offset: 0,
          limit: 2000,
        }
      : undefined,
  );

  const allSchedules =
    tagId && !persistOnSave
      ? (data?.result || []).map((schedule) => ({
          localId: schedule.id,
          id: schedule.id,
          tagId: schedule.tagId,
          brands: schedule.brands,
          regions: schedule.regions,
          activeStartDate: schedule.activeStartDate,
          activeEndDate: schedule.activeEndDate,
          travelStartDate: schedule.travelStartDate,
          travelEndDate: schedule.travelEndDate,
        }))
      : schedules.map((schedule) => ({
          ...schedule,
          tagId: schedule.tagId || '',
        }));

  const createRow: TagDraftSchedule = {
    localId: CREATE_SCHEDULE_ROW_ID,
    brands: createValues.brands,
    regions: createValues.regions,
    activeStartDate: createValues.activeStartDate || '',
    activeEndDate: createValues.activeEndDate || '',
    travelStartDate: createValues.travelStartDate || undefined,
    travelEndDate: createValues.travelEndDate || undefined,
  };

  const rows = [...allSchedules, createRow];

  const isCreateRow = (row: TagDraftSchedule) => row.localId === CREATE_SCHEDULE_ROW_ID;
  const isEditingRow = (row: TagDraftSchedule) => !!editingSchedule && row.localId === editingSchedule.localId;

  const createFieldUpdater =
    (setter: React.Dispatch<React.SetStateAction<TagScheduleFormData>>) =>
    <K extends keyof TagScheduleFormData>(field: K, value: TagScheduleFormData[K]) => {
      setter((currentValues) => ({
        ...currentValues,
        [field]: value,
      }));
    };

  const updateCreateValues = createFieldUpdater(setCreateValues);
  const updateEditValues = createFieldUpdater(setEditValues);

  const handleRegionsChange = (
    selectedValues: Array<string>,
    currentValues: TagScheduleFormData,
    onChange: (nextValues: Array<string>) => void,
  ) => {
    const isAllRegionsSelected = currentValues.regions.length === allRegionCodes.length;

    if (selectedValues.includes(SELECT_ALL_VALUE)) {
      onChange(isAllRegionsSelected ? [] : allRegionCodes);
      return;
    }

    onChange(selectedValues);
  };

  const handleCreateSave = async () => {
    const result = await onSaveCreate(createValues);

    if (result) {
      setCreateValues(getScheduleFormValues());
    }
  };

  const handleEditSave = async () => {
    const result = await onSaveEdit(editValues);

    if (result) {
      setEditValues(getScheduleFormValues());
    }
  };

  const renderBrandsInput = (values: TagScheduleFormData) => {
    const brands = values.brands.length > 0 ? values.brands : ['luxuryescapes'];

    return (
      <FormControl fullWidth size="small">
        <InputLabel shrink>Brands</InputLabel>
        <Select
          multiple
          value={brands}
          displayEmpty
          disabled
          label="Brands"
          input={<OutlinedInput notched label="Brands" />}
          renderValue={(selected) => (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
        >
          {brands.map((brand) => (
            <MenuItem key={brand} value={brand}>
              {brand}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderRegionsInput = (values: TagScheduleFormData, onChange: (nextValues: Array<string>) => void) => {
    const isAllRegionsSelected = values.regions.length === allRegionCodes.length;

    return (
      <FormControl fullWidth size="small">
        <InputLabel required>Regions</InputLabel>
        <Select
          multiple
          value={values.regions}
          onChange={(event) => handleRegionsChange(event.target.value as Array<string>, values, onChange)}
          input={<OutlinedInput label="Regions" />}
          renderValue={(selected) => (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {selected.length === allRegionCodes.length ? (
                <Chip label="All Regions" size="small" />
              ) : (
                selected.map((code) => {
                  const region = regionOptions.find((option) => option.code === code);
                  return <Chip key={code} label={region?.name || code} size="small" />;
                })
              )}
            </Box>
          )}
        >
          <MenuItem value={SELECT_ALL_VALUE}>
            <Checkbox
              checked={isAllRegionsSelected}
              indeterminate={values.regions.length > 0 && !isAllRegionsSelected}
            />
            <ListItemText primary="Select All" />
          </MenuItem>
          {regionOptions.map((region) => (
            <MenuItem key={region.code} value={region.code}>
              <Checkbox checked={values.regions.includes(region.code)} />
              <ListItemText primary={region.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderDateInput = (props: {
    label: string;
    value: string | null;
    minDate?: string | null;
    onChange: (nextValue: string | null) => void;
  }) => (
    <DatePicker
      label={props.label}
      value={props.value ? dayjs(props.value) : null}
      minDate={props.minDate ? dayjs(props.minDate) : undefined}
      onChange={(date) => props.onChange(date ? formatDateLongISO(date.toDate()) : null)}
      slotProps={{
        textField: {
          fullWidth: true,
          size: 'small',
        },
      }}
    />
  );

  const columns: Array<GridColDef<TagDraftSchedule>> = [
    {
      field: 'brands',
      headerName: 'Brands',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams<TagDraftSchedule>) => {
        if (isCreateRow(params.row)) {
          return renderBrandsInput(createValues);
        }

        if (isEditingRow(params.row)) {
          return renderBrandsInput(editValues);
        }

        return (
          <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" height="100%">
            {params.row.brands.map((brand) => (
              <Chip key={brand} label={brand} size="small" />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'regions',
      headerName: 'Regions',
      flex: 1.5,
      minWidth: 220,
      sortable: false,
      renderCell: (params: GridRenderCellParams<TagDraftSchedule>) => {
        if (isCreateRow(params.row)) {
          return renderRegionsInput(createValues, (nextValues) => updateCreateValues('regions', nextValues));
        }

        if (isEditingRow(params.row)) {
          return renderRegionsInput(editValues, (nextValues) => updateEditValues('regions', nextValues));
        }

        const regions = params.row.regions || [];
        const isAllRegions = regions.length === allRegionCodes.length;

        if (regions.length === 0) {
          return (
            <Box display="flex" alignItems="center" height="100%">
              N/A
            </Box>
          );
        }

        if (isAllRegions) {
          return (
            <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" height="100%">
              <Chip label="All Regions" size="small" />
            </Box>
          );
        }

        const displayRegions = regions.slice(0, 3);
        const remainingCount = regions.length - 3;
        const remainingRegionNames = regions
          .slice(3)
          .map((code) => regionOptions.find((region) => region.code === code)?.name || code)
          .join(', ');

        return (
          <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" height="100%">
            {displayRegions.map((code) => {
              const region = regionOptions.find((option) => option.code === code);
              return <Chip key={code} label={region?.name || code} size="small" />;
            })}
            {remainingCount > 0 && (
              <Tooltip title={remainingRegionNames}>
                <Chip label={`+${remainingCount} more`} size="small" variant="outlined" />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'activeStartDate',
      headerName: 'Active Start',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams<TagDraftSchedule>) => {
        if (isCreateRow(params.row)) {
          return renderDateInput({
            label: 'Active Start',
            value: createValues.activeStartDate,
            onChange: (nextValue) => updateCreateValues('activeStartDate', nextValue),
          });
        }

        if (isEditingRow(params.row)) {
          return renderDateInput({
            label: 'Active Start',
            value: editValues.activeStartDate,
            onChange: (nextValue) => updateEditValues('activeStartDate', nextValue),
          });
        }

        return (
          <Box display="flex" alignItems="center" height="100%">
            {params.row.activeStartDate ? formatDateLongMonthWithMeridiemShort(params.row.activeStartDate) : 'N/A'}
          </Box>
        );
      },
    },
    {
      field: 'activeEndDate',
      headerName: 'Active End',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams<TagDraftSchedule>) => {
        if (isCreateRow(params.row)) {
          return renderDateInput({
            label: 'Active End',
            value: createValues.activeEndDate,
            minDate: createValues.activeStartDate,
            onChange: (nextValue) => updateCreateValues('activeEndDate', nextValue),
          });
        }

        if (isEditingRow(params.row)) {
          return renderDateInput({
            label: 'Active End',
            value: editValues.activeEndDate,
            minDate: editValues.activeStartDate,
            onChange: (nextValue) => updateEditValues('activeEndDate', nextValue),
          });
        }

        return (
          <Box display="flex" alignItems="center" height="100%">
            {params.row.activeEndDate ? formatDateLongMonthWithMeridiemShort(params.row.activeEndDate) : 'N/A'}
          </Box>
        );
      },
    },
    {
      field: 'travelStartDate',
      headerName: 'Travel Start',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams<TagDraftSchedule>) => {
        if (isCreateRow(params.row)) {
          return renderDateInput({
            label: 'Travel Start',
            value: createValues.travelStartDate,
            onChange: (nextValue) => updateCreateValues('travelStartDate', nextValue),
          });
        }

        if (isEditingRow(params.row)) {
          return renderDateInput({
            label: 'Travel Start',
            value: editValues.travelStartDate,
            onChange: (nextValue) => updateEditValues('travelStartDate', nextValue),
          });
        }

        return (
          <Box display="flex" alignItems="center" height="100%">
            {params.row.travelStartDate ? formatDateSlashes(params.row.travelStartDate) : 'N/A'}
          </Box>
        );
      },
    },
    {
      field: 'travelEndDate',
      headerName: 'Travel End',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams<TagDraftSchedule>) => {
        if (isCreateRow(params.row)) {
          return renderDateInput({
            label: 'Travel End',
            value: createValues.travelEndDate,
            minDate: createValues.travelStartDate,
            onChange: (nextValue) => updateCreateValues('travelEndDate', nextValue),
          });
        }

        if (isEditingRow(params.row)) {
          return renderDateInput({
            label: 'Travel End',
            value: editValues.travelEndDate,
            minDate: editValues.travelStartDate,
            onChange: (nextValue) => updateEditValues('travelEndDate', nextValue),
          });
        }

        return (
          <Box display="flex" alignItems="center" height="100%">
            {params.row.travelEndDate ? formatDateSlashes(params.row.travelEndDate) : 'N/A'}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 220,
      sortable: false,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams<TagDraftSchedule>) => {
        if (isCreateRow(params.row)) {
          return (
            <Stack direction="row" spacing={1} width="100%" justifyContent="flex-end">
              <Button variant="text" onClick={handleCreateSave} disabled={isSavingInline}>
                Add
              </Button>
            </Stack>
          );
        }

        if (isEditingRow(params.row)) {
          return (
            <Stack direction="row" spacing={1} width="100%" justifyContent="flex-end">
              <Button variant="text" onClick={onCancelEdit} disabled={isSavingInline}>
                Cancel
              </Button>
              <Button variant="text" onClick={handleEditSave} disabled={isSavingInline}>
                Save
              </Button>
            </Stack>
          );
        }

        return (
          <Stack direction="row" spacing={1} width="100%" justifyContent="flex-end">
            <Button variant="text" onClick={() => onEditClick(params.row)} disabled={isSavingInline}>
              Edit
            </Button>
            {onEndSchedule && (
              <Button variant="text" color="error" onClick={() => onEndSchedule(params.row)} disabled={isSavingInline}>
                End
              </Button>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <Box>
      {!!error && <Alert severity="error">{error?.message}</Alert>}
      <Box sx={DATAGRID_BORDERED_CONTAINER_SX}>
        <DataGrid
          loading={isLoading && !!tagId && !persistOnSave}
          rows={rows}
          columns={columns}
          getRowId={(row: TagDraftSchedule) => row.localId}
          getRowHeight={() => 'auto'}
          hideFooter
          disableRowSelectionOnClick
          sx={[
            { border: 0 },
            DATAGRID_EXPAND_TO_ROWS_SX,
            {
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
                py: 1,
              },
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
                outline: 'none',
              },
            },
            DATAGRID_HIDE_SEPARATORS_SX,
          ]}
        />
      </Box>
    </Box>
  );
}
